import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { stockRequestsApi, warehousesApi } from '../../api/warehouse';
import { ApiError } from '../../api/client';
import { PageHeader } from '../../components/PageHeader';
import { SectionHeader } from '../../components/SectionHeader';
import { DataTable } from '../../components/DataTable';
import { StatusBadge } from '../../components/StatusBadge';
import { useToast } from '../../components/Toast';
import { EmployeePicker } from '../../components/EmployeePicker';
import { formatDate } from '../../utils/formatDate';

export const STOCK_REQUEST_STATUS_LABELS: Record<string, string> = {
  pending: 'Pending', approved: 'Approved', partially_issued: 'Partially Issued', issued: 'Issued', rejected: 'Rejected', cancelled: 'Cancelled',
};

interface CreateStockRequestForm { warehouseId: number; requesterEmployeeId: number; purpose: string; requiredDate: string; }

export function StockRequestsListPage() {
  const [showCreateForm, setShowCreateForm] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();
  const { register, handleSubmit, reset } = useForm<CreateStockRequestForm>();

  const { data, isLoading, isError } = useQuery({ queryKey: ['stock-requests'], queryFn: () => stockRequestsApi.list() });
  const { data: warehousesData } = useQuery({ queryKey: ['warehouses'], queryFn: () => warehousesApi.list() });

  const createMutation = useMutation({
    mutationFn: (input: CreateStockRequestForm) => stockRequestsApi.create({ ...input, warehouseId: Number(input.warehouseId) }),
    onSuccess: (result) => {
      showSuccess(`Stock request ${result.data.requestReference} created.`);
      queryClient.invalidateQueries({ queryKey: ['stock-requests'] });
      setShowCreateForm(false);
      reset();
      navigate(`/logistics/stock-requests/${result.data.id}`);
    },
    onError: (err: ApiError) => showError(err.message),
  });

  const requests = data?.data ?? [];
  const warehouses = warehousesData?.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Stock Requests" description="Request stock from a warehouse for a project or operational need." />

      <SectionHeader title="Requests" actions={
        <button onClick={() => setShowCreateForm((s) => !s)}>{showCreateForm ? 'Cancel' : 'New Stock Request'}</button>
      } />

      {showCreateForm && (
        <form className="inline-form" onSubmit={handleSubmit((d) => createMutation.mutate(d))}>
          <label>Warehouse
            <select {...register('warehouseId', { required: true, valueAsNumber: true })}>
              <option value="">Select…</option>
              {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
            </select>
          </label>
          <label>Requester <EmployeePicker {...register('requesterEmployeeId', { required: true, valueAsNumber: true })} /></label>
          <label>Purpose <input {...register('purpose', { required: true })} /></label>
          <label>Required Date <input type="date" {...register('requiredDate', { required: true })} /></label>
          <button type="submit" disabled={createMutation.isPending}>Create</button>
        </form>
      )}

      {isError && <div className="error-state">Failed to load stock requests. Please try refreshing the page.</div>}
      {isLoading ? <div className="loading-state"><span className="loading-spinner" />Loading…</div> : (
        <DataTable
          rows={requests}
          rowKey={(r) => r.id}
          columns={[
            { header: 'Reference', render: (r) => <a onClick={() => navigate(`/logistics/stock-requests/${r.id}`)} className="table-link">{r.request_reference}</a> },
            { header: 'Purpose', render: (r) => r.purpose },
            { header: 'Required Date', render: (r) => formatDate(r.required_date) },
            { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STOCK_REQUEST_STATUS_LABELS[r.status]} /> },
          ]}
        />
      )}
    </div>
  );
}
